/**
 * The presentational pieces of the Going PIN sheet (TECH-663): the PIN card body and the chip that
 * carries the previous card's outcome. No state, no effects: `EventPinSheet` owns the steps.
 */
import React from 'react';
import { Check } from 'lucide-react';
import { INK_SOLID_BUTTON_CLASS } from '@components/ui/buttonVariants';
import { DrawerDescription, DrawerTitle } from '@components/ui/drawer';
import { ImgWithFallback } from '@components/ui/img-with-fallback';
import type { PinRequiredSpace } from '@models/api';
import { EventWhen } from '@components/community/EventWhen';
import { PinEntryRow } from '@components/community/PinEntryRow';
import type { EventWhenInput } from '@utils/datetime';
import { getUserInitials } from '@utils/userAvatar';
import type { Chip } from './useEventPinSteps';
import {
  goingTitle,
  hostedBy,
  join,
  joinIntro,
  joinedChip,
  joinedChipLead,
  joining,
  pinLabel,
  pinPlaceholder,
  requestedChip,
  requestedChipLead,
} from './eventPinCopy';

/**
 * The filled button of the sheet: the AA-passing mb-teal-ink ladder (ui-ux-designer), 52px tall at
 * least. It grows rather than clips with large text (browser zoom, Android's font size): a fixed
 * height and Button's nowrap pushed a long label past both rounded ends.
 */
export const PRIMARY_BUTTON_CLASS = `min-h-13 h-auto whitespace-normal py-3 text-balance w-full rounded-full ${INK_SOLID_BUTTON_CLASS} font-fira text-base font-medium focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2`;
/**
 * The outline button: Join once Request access has become the one primary. `active:` is the press
 * feedback on touch, which has no hover (mobile-native §2); Button's transition-colors runs it. It
 * is the hover fill: mb-teal-pale carries the ink label at AA (its note in tailwind.css).
 */
export const OUTLINE_BUTTON_CLASS =
  'h-13 w-full rounded-full border-2 border-mb-teal-ink bg-white font-fira text-base font-medium text-mb-teal-ink hover:bg-mb-teal-pale hover:text-mb-teal-ink active:bg-mb-teal-pale focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2';
/**
 * The same footprint as a text button, so the footer does not move when it turns primary, growing
 * with large text the same way. No shadow: Button's default variant brings shadow-sm, which a
 * transparent pill would show.
 */
export const TEXT_BUTTON_CLASS =
  'min-h-13 h-auto whitespace-normal py-3 text-balance w-full rounded-full bg-transparent font-fira text-base font-medium text-mb-teal-ink shadow-none hover:bg-mb-teal-pale hover:text-mb-teal-ink active:bg-mb-teal-pale focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2';

/**
 * The scrolling part of a step. `motionClass` plays when `animationKey` changes (a new card, or the
 * ticket step): the wrapper remounts, which also resets the PIN typed on the previous card.
 */
export const SheetBody: React.FC<{
  animationKey: string;
  motionClass: string | undefined;
  children: React.ReactNode;
}> = ({ animationKey, motionClass, children }) => (
  // data-pin-sheet-scroll: the sheet's swipe-to-dismiss reads this element's scrollTop, and the
  // keyboard reveal (useKeyboardSheetCap) scrolls only this element.
  <div data-pin-sheet-scroll="" className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
    {/* overflow-x: clip, not hidden: the sliding step adds no scrollable overflow, so a focus()
        during the slide (the card's autoFocus, the ticket title) cannot scroll the body sideways
        and cancel the slide (Chromium) or reverse it (WebKit). Safari 16+, Chrome 90+. */}
    <div className="overflow-x-clip">
      <div key={animationKey} className={motionClass}>
        {children}
      </div>
    </div>
  </div>
);

/** The pinned footer: the step's buttons, bottom-aligned at the same place on every step. */
export const SheetFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-21 shrink-0 flex-col justify-end gap-3 px-5 pt-3 pb-safe-offset-4">
    {children}
  </div>
);

/**
 * The previous card's outcome, at the top of the next step. One pill line that never outgrows the
 * column: a long Space name truncates at the end (the full name was on the card before, and stays
 * in the title), and the tick, the Lucide Check the answer line uses, never shrinks. The tick is
 * decoration: the words carry the outcome to a screen reader. The name is in a `<bdi>`, so a
 * right-to-left name keeps its own punctuation on its own side inside the English line.
 */
export const PinChip: React.FC<{ chip: Chip }> = ({ chip }) => {
  const joined = chip.tone === 'joined';
  const text = joined ? joinedChip(chip.spaceName) : requestedChip(chip.spaceName);
  return (
    <p className="mx-auto flex w-fit max-w-full items-center gap-1 rounded-full bg-mb-teal-pale px-3 py-1 font-fira text-sm font-medium text-mb-teal-ink">
      <span className="min-w-0 truncate" title={text}>
        {joined ? joinedChipLead : requestedChipLead} <bdi>{chip.spaceName}</bdi>
      </span>
      {chip.tone === 'joined' ? <Check aria-hidden="true" className="size-4 flex-none" /> : null}
    </p>
  );
};

/**
 * What sits under the host's name: the PIN entry; the lockout in its place once a 429 has locked
 * the card (no PIN may be sent from it again); or the card's answer once there is one. The lockout
 * line's `id` is what Request access names as its description (`aria-describedby`): focus moves
 * there in the same frame, so the reason rides with the focus rather than in an alert it would cut.
 */
export type HostRowContent =
  | {
      kind: 'pin';
      submitting: boolean;
      error: string | null;
      shakeKey: number;
      /** False once Request access is the primary button: one primary at a time. */
      submitPrimary: boolean;
    }
  | { kind: 'locked'; text: string; id: string }
  | { kind: 'answer'; text: string; tick: boolean };

/** What the card shows of the Event: its name and when it is. */
export type PinCardEvent = EventWhenInput & { name: string };

/**
 * The event block under the title: its name (up to two lines), then when it is, on its own line
 * so a date is never clipped. While the Event is first read, two placeholder bars hold that space,
 * so the PIN field does not move under a finger when it lands.
 */
const EventBlock: React.FC<{ event: PinCardEvent | 'loading' }> = ({ event }) =>
  event === 'loading' ? (
    <div
      data-pin-event-placeholder=""
      aria-hidden="true"
      className="flex w-full flex-col items-center gap-1.5"
    >
      <div className="my-0.5 h-4 w-48 max-w-full animate-pulse rounded bg-muted" />
      <div className="my-0.5 h-4 w-36 max-w-full animate-pulse rounded bg-muted" />
    </div>
  ) : (
    <>
      {/* dir="auto": a right-to-left name keeps its punctuation on its own side (break-ui, RTL). */}
      <p
        dir="auto"
        className="line-clamp-2 max-w-full font-fira text-sm text-mb-dark-gray wrap-anywhere"
      >
        {event.name}
      </p>
      <EventWhen
        event={event}
        variant="date-time"
        className="font-fira text-sm text-mb-dark-gray"
      />
    </>
  );

interface EventPinCardProps {
  space: PinRequiredSpace;
  /**
   * The Event once read; 'loading' while it is first read (placeholders hold its place); null when
   * it cannot be read (the block is left out).
   */
  event: PinCardEvent | 'loading' | null;
  chip: Chip | null;
  row: HostRowContent;
  onSubmitPin: (pin: string) => void;
}

export const EventPinCard: React.FC<EventPinCardProps> = ({
  space,
  event,
  chip,
  row,
  onSubmitPin,
}) => (
  <div className="flex flex-col gap-5 px-5 pt-4 pb-2">
    {chip ? <PinChip chip={chip} /> : null}
    <div className="flex flex-col items-center gap-1.5 text-center">
      <DrawerTitle className="font-livvic text-2xl font-bold text-black">{goingTitle}</DrawerTitle>
      {event ? <EventBlock event={event} /> : null}
    </div>
    <DrawerDescription className="text-center font-fira text-sm text-mb-dark-gray">
      {joinIntro}
    </DrawerDescription>
    <div className="flex flex-col gap-3">
      <p className="font-fira text-sm text-mb-dark-gray">{hostedBy}</p>
      <div className="flex items-center gap-3">
        <div className="flex size-11 flex-none items-center justify-center overflow-hidden rounded-full bg-mb-teal-pale font-fira text-sm font-bold text-mb-teal-ink">
          <ImgWithFallback
            src={space.logo_url}
            alt=""
            className="size-full object-cover"
            fallback={() => <span aria-hidden="true">{getUserInitials(space.space_name)}</span>}
          />
        </div>
        {/* Clamped at two lines, so the full name rides in `title`; dir="auto" for a right-to-left
            name (break-ui: RTL, and truncated text with no way to read it). */}
        <span
          dir="auto"
          title={space.space_name}
          className="line-clamp-2 min-w-0 font-fira text-base font-medium text-black wrap-anywhere"
        >
          {space.space_name}
        </span>
      </div>
      {row.kind === 'pin' ? (
        <PinEntryRow
          label={pinLabel(space.space_name)}
          placeholder={pinPlaceholder}
          submitLabel={join}
          submittingLabel={joining}
          isSubmitting={row.submitting}
          error={row.error}
          shakeKey={row.shakeKey}
          onSubmit={onSubmitPin}
          autoFocus
          submitClassName={row.submitPrimary ? PRIMARY_BUTTON_CLASS : OUTLINE_BUTTON_CLASS}
        />
      ) : null}
      {row.kind === 'locked' ? (
        <p id={row.id} className="text-center font-fira text-sm text-destructive">
          {row.text}
        </p>
      ) : null}
      {/* The answer replaces the entry and is announced (D10: the timers always run, so a screen
          reader hears the outcome instead of racing a countdown). The region is mounted, empty,
          with the card, so the answer is an insertion the reader notices. */}
      <div aria-live="polite">
        {row.kind === 'answer' ? (
          <p className="flex min-h-12 items-center justify-center gap-2 text-center font-fira text-base font-medium text-mb-teal-ink">
            {row.tick ? <Check aria-hidden="true" className="size-5 flex-none" /> : null}
            {row.text}
          </p>
        ) : null}
      </div>
    </div>
  </div>
);
